import { ProductWithCalculations } from '../shared/types/inventory';
import {CardHelp} from '../shared/components/CardHelp';

interface MetricsPanelProps {
  product: ProductWithCalculations;
  leadTime: number;
  targetCoverage: number;
}

export const MetricsPanel = ({ product, leadTime, targetCoverage }: MetricsPanelProps) => {
  const metrics = [
    {
      label: 'Estoque Atual',
      value: product.estoqueAtual.toFixed(0),
      unit: 'un',
      color: 'text-slate-900',
      bgColor: 'bg-slate-50',
      help: 'Soma disponível atualmente para este produto.',
    },
    {
      label: 'CMD',
      value: product.cmd.toFixed(2),
      unit: 'un/dia',
      color: 'text-blue-700',
      bgColor: 'bg-blue-50',
      help: 'Consumo médio diário = vendas do período ÷ quantidade de dias do período.',
    },
    {
      label: 'ROP',
      value: product.rop.toFixed(0),
      unit: 'un',
      color: 'text-amber-700',
      bgColor: 'bg-amber-50',
      help: 'Ponto de pedido = demanda durante o lead time + estoque de segurança.',
    },
    {
      label: 'Estoque Alvo',
      value: product.estoqueAlvo.toFixed(0),
      unit: 'un',
      color: 'text-emerald-700',
      bgColor: 'bg-emerald-50',
      help: 'Quantidade planejada para atender a cobertura alvo configurada.',
    },
    {
      label: 'Estoque de Segurança',
      value: product.ss.toFixed(0),
      unit: 'un',
      color: 'text-slate-700',
      bgColor: 'bg-slate-50',
      help: 'Reserva calculada para absorver variações e imprevistos.',
    },
    {
      label: 'Cobertura Atual',
      value: product.cobertura.toFixed(1),
      unit: 'dias',
      color: 'text-violet-700',
      bgColor: 'bg-violet-50',
      help: 'Cobertura atual = estoque atual ÷ consumo médio diário.',
    },
  ];

  return (
    <div className="space-y-4">
      <div className="relative rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 p-6 text-white shadow-lg">
        <CardHelp content="Pedido sugerido = estoque alvo menos estoque atual, limitado ao mínimo de zero." className="absolute right-2 top-2 text-white/70 hover:bg-white/10 hover:text-white" />
        <div className="text-sm font-medium opacity-90 mb-1">Pedido Sugerido</div>
        <div className="text-4xl font-bold tracking-tight">{product.pedidoSugerido.toFixed(0)}</div>
        <div className="text-sm opacity-80 mt-1">unidades</div>
        <div className="text-xs opacity-75 mt-3 pt-3 border-t border-white/20">
          = Estoque Alvo - Estoque Atual
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {metrics.map((metric) => (
          <div
            key={metric.label}
            className={`${metric.bgColor} relative rounded-xl border border-slate-200/50 p-4`}
          >
            <CardHelp content={metric.help} className="absolute right-2 top-2" />
            <div className="pr-7 text-xs font-medium text-slate-600 mb-1">{metric.label}</div>
            <div className="flex items-baseline gap-1">
              <span className={`text-2xl font-bold ${metric.color}`}>{metric.value}</span>
              <span className="text-xs text-slate-500">{metric.unit}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="relative rounded-xl border border-slate-200 bg-slate-50 p-4">
        <CardHelp content="Parâmetros usados no cálculo deste produto. O lead time pode vir do fornecedor; a cobertura alvo vem das configurações." className="absolute right-2 top-2" />
        <div className="mb-3 pr-7 text-xs font-semibold text-slate-700">Configurações</div>
        <div className="space-y-2 text-xs text-slate-600">
          <div className="flex justify-between">
            <span>Lead Time (LT):</span>
            <span className="font-semibold text-slate-900">{leadTime} dias</span>
          </div>
          <div className="flex justify-between">
            <span>Cobertura Alvo:</span>
            <span className="font-semibold text-slate-900">{targetCoverage} dias</span>
          </div>
          <div className="flex justify-between">
            <span>Vendas no Período:</span>
            <span className="font-semibold text-slate-900">{product.vendasPeriodo} un</span>
          </div>
        </div>
      </div>
    </div>
  );
};
